import React from "react";
import { Link } from "react-router-dom";
import { Download, Music, Users, MapPin, Mail, Phone, Star, Radio } from "lucide-react";
import Reveal from "../components/Reveal";
import { IMAGES, CONTACT_INFO, SETLIST_HIGHLIGHTS } from "../mock/mock";
import { useToast } from "../hooks/use-toast";

const EPK = () => {
  const { toast } = useToast();
  const notify = () => toast({ title: "Press Kit", description: "The full press kit PDF would download here (mocked in this demo)." });

  return (
    <div className="overflow-hidden">
      <section className="pt-40 pb-16 px-5 text-center relative">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[500px] h-[400px] bg-[#e0b04c]/8 rounded-full blur-[130px] pointer-events-none" />
        <Reveal className="relative z-10 max-w-3xl mx-auto">
          <p className="text-[#e0b04c] text-sm tracking-[0.3em] uppercase font-medium mb-5">Electronic Press Kit</p>
          <h1 className="font-serif-display text-5xl md:text-6xl text-[#f5f0e8] leading-tight">
            A.T.O.M. <span className="text-gold-gradient">Press Kit</span>
          </h1>
          <p className="text-[#b3a998] text-lg leading-relaxed mt-6 max-w-2xl mx-auto">
            Everything you need to book and promote A.T.O.M. — bio, stats,
            technical details, and high-resolution assets.
          </p>
          <button onClick={notify} className="mt-8 inline-flex items-center gap-2 bg-gradient-to-b from-[#e8bc55] to-[#c99339] text-[#0c0a08] font-semibold px-8 py-4 rounded-md hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(224,176,76,0.4)] transition-all duration-300">
            <Download size={18} /> Download Full Press Kit
          </button>
        </Reveal>
      </section>

      {/* BIO + IMAGE */}
      <section className="px-5 pb-20">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <Reveal className="relative group">
            <div className="absolute -inset-3 bg-gradient-to-tr from-[#e0b04c]/20 to-transparent rounded-xl blur-xl opacity-60 group-hover:opacity-100 transition-opacity duration-500" />
            <img src={IMAGES.epk} alt="A.T.O.M. promotional photo" className="relative rounded-xl w-full object-cover shadow-2xl" />
          </Reveal>
          <Reveal delay={150}>
            <h2 className="font-serif-display text-3xl text-[#f5f0e8] mb-5">The Band, In Brief</h2>
            <p className="text-[#b3a998] leading-relaxed mb-4">
              A.T.O.M. (A Tribute to Maze) is a full-band, concert-ready tribute
              to the legendary Frankie Beverly &amp; Maze. Comprised of seasoned
              soul, R&amp;B, funk, and gospel musicians, the group delivers an
              authentic live experience — no backing tracks, no shortcuts.
            </p>
            <p className="text-[#b3a998] leading-relaxed">
              Based in Washington, DC and performing nationwide, A.T.O.M. has
              graced festivals, theaters, casinos, cruises, and private
              celebrations, turning every audience into one big family.
            </p>
          </Reveal>
        </div>
      </section>

      {/* QUICK STATS */}
      <section className="py-16 px-5 bg-[#0a0806]/60 border-y border-white/5">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: Users, n: "7-9", l: "Piece Ensemble" },
            { icon: Music, n: "20+", l: "Signature Songs" },
            { icon: Radio, n: "90-120", l: "Minute Sets" },
            { icon: MapPin, n: "USA", l: "National Reach" },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 90}>
              <div className="text-center p-6 rounded-xl bg-[#12100c] border border-white/5">
                <s.icon className="text-[#e0b04c] mx-auto mb-3" size={28} />
                <div className="font-serif-display text-3xl text-gold-gradient">{s.n}</div>
                <div className="text-[#8f8577] text-xs uppercase tracking-widest mt-1">{s.l}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* REPERTOIRE + TECH */}
      <section className="py-20 px-5">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10">
          <Reveal>
            <div className="h-full p-8 rounded-xl bg-[#12100c] border border-white/5">
              <h3 className="font-serif-display text-2xl text-[#f5f0e8] mb-5 flex items-center gap-3"><Star className="text-[#e0b04c]" size={22} /> Repertoire Highlights</h3>
              <div className="grid grid-cols-2 gap-2.5">
                {SETLIST_HIGHLIGHTS.map((s) => (
                  <div key={s} className="flex items-center gap-2 text-[#d8cdb9] text-sm"><Music size={14} className="text-[#e0b04c]" /> {s}</div>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="h-full p-8 rounded-xl bg-[#12100c] border border-white/5">
              <h3 className="font-serif-display text-2xl text-[#f5f0e8] mb-5 flex items-center gap-3"><Radio className="text-[#e0b04c]" size={22} /> Technical &amp; Production</h3>
              <ul className="space-y-3 text-[#c9bda9] text-sm">
                {["Full band arrives early for professional soundcheck", "Coordinates directly with your production team", "Professional attire — signature all-white option", "Detailed stage plot & input list provided on booking", "Clear communication from booking to load-out"].map((t) => (
                  <li key={t} className="flex gap-3"><span className="text-[#e0b04c]">&bull;</span> {t}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CONTACT STRIP */}
      <section className="py-16 px-5 bg-[#0a0806]/60 border-t border-white/5">
        <Reveal className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif-display text-3xl text-[#f5f0e8] mb-8">Booking Contact</h2>
          <div className="flex flex-col md:flex-row justify-center gap-6 mb-10">
            <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center justify-center gap-3 text-[#c9bda9] hover:text-[#e0b04c] transition-colors"><Mail className="text-[#e0b04c]" size={20} /> {CONTACT_INFO.email}</a>
            {CONTACT_INFO.phones.map((p) => (
              <a key={p} href={`tel:${p}`} className="flex items-center justify-center gap-3 text-[#c9bda9] hover:text-[#e0b04c] transition-colors"><Phone className="text-[#e0b04c]" size={20} /> {p}</a>
            ))}
          </div>
          <Link to="/booking" className="inline-flex bg-gradient-to-b from-[#e8bc55] to-[#c99339] text-[#0c0a08] font-semibold px-8 py-4 rounded-md hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(224,176,76,0.4)] transition-all duration-300">Request Availability</Link>
        </Reveal>
      </section>
    </div>
  );
};

export default EPK;
